import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { EFilingDocument, DocumentMetadataItem, EfmDocumentType, EfmDocumentTitle, ExtractionResult } from '@lds-efsp/shared-contracts';
import { steerAwayFromInitiationOnly, PARTY_CLASS_CODES } from '@lds-efsp/shared-contracts';
import { useEFiling } from '../contexts/EFilingContext';
import { shouldWarnDocClassNotOffered } from '../utils/doc-class-match';
import { buildAssignmentFillPatch, isCourtSourcedRoster } from '../lib/assignment-autofill';
import { documentCardIncomplete } from '../lib/document-card-state';
import { applyRefersToRequirement } from '../lib/refers-to-required';
import { resetCourtScopedCaseData } from '../lib/reset-court-scoped-case';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { SessionExpiredError } from '../../../shared/contexts/SessionExpiryContext';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';
import DocumentMetadataForm from './DocumentMetadataForm';
import OcrConfirmCards from './OcrConfirmCards';
import FilingTypeDetection from './FilingTypeDetection';
import CaseAttachPanel from './CaseAttachPanel';
import InlineCaseTypeSelector from './InlineCaseTypeSelector';
import InlineCourtSelector from './InlineCourtSelector';
import SectionConfirmControl from './SectionConfirmControl';
import DocTypeCombobox from './DocTypeCombobox';

const API_BASE = API_BASE_URL;

// Extended doc type with metadata and formGroups from API response
interface DocTypeWithMetadata extends EfmDocumentType {
  metadata?: DocumentMetadataItem[];
  formGroups?: string[];
  efmRequiresSubCase?: boolean;
}

interface DocumentsTabProps {
  brand: BrandConfig;
}

// WOL-329 item 4 (case-type suggestion hint) ships DORMANT. Flip
// VITE_OCR_CASETYPE_HINT=1 at build time to surface the non-committal "we
// detected a Complaint — pick the matching case type" hint in the picker. Off =
// no behavior change. (Hint only; it never auto-selects a case type.)
const CASETYPE_HINT_ENABLED = import.meta.env.VITE_OCR_CASETYPE_HINT === '1';

// "Is a reference" — the repo's ONE reference shape for a party-class slot: a
// non-empty all-strings array under metadataValues[code], the identical guard
// EFilingContext's `holdsReference`, `getAssignmentProvenance` and the remap's
// all-strings filter apply. A scalar codeList value and a new-data object
// array hold no roster reference.
const holdsPartyReference = (v: unknown): boolean =>
  Array.isArray(v) && v.length > 0 && v.every(x => typeof x === 'string');

const DocumentsTab: React.FC<DocumentsTabProps> = ({ brand }) => {
  const {
    wizardState,
    addDocument,
    removeDocument,
    updateDocument,
    updateDocumentMetadata,
    uploadDocument,
    uploadStatuses,
    extractionStatus,
    setExtractionStatus,
    applyExtraction,
    ocrPopulatedFields,
    confirmOcrField,
    updateOrderInfo,
    updateCaseInfo,
    setParties,
    setAttorneys,
    registerDocTypeMetadata,
    confirmFlowEnabled,
    revertCaseAttachSections,
  } = useEFiling();
  const { documents, parties } = wizardState;
  // R18 input to the shared per-doc validator (same derivation as validateTab):
  // the card's red state below routes through it so gate and card agree.
  const selfRepPartyIds = useMemo(
    () => new Set(parties.filter(p => p.isSelfRepresented).map(p => p.id)),
    [parties],
  );
  const { caseCategoryCode, caseCategoryName, ocrExtractedCategoryName, caseType, caseTrackingId, trialCourtCaseNumber } = wizardState.caseInfo;
  const { filingType, county, state: filingState } = wizardState.orderInfo;
  const { fetchWithAuth } = useApi();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingDocType, setEditingDocType] = useState<Set<string>>(() => new Set());
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [extractionNotice, setExtractionNotice] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // "Change" on an OCR-detected Court / Case Category & Type card opens the
  // IN-CARD picker instead of navigating to a later step (Walter R26 — no
  // control on this step may jump ahead before per-document types are set).
  const [courtPickerForced, setCourtPickerForced] = useState(false);
  const [caseTypePickerForced, setCaseTypePickerForced] = useState(false);
  // SOR-162: bumped when the filer detaches the attached case on this step, to
  // force a fresh CaseAttachPanel mount. CaseSearch keeps its selected-case
  // preview in component-local state, so wiping wizard state alone would leave a
  // stale "Selected: <case>" banner on screen — remounting is what clears it.
  const [caseSearchNonce, setCaseSearchNonce] = useState(0);

  // Document type taxonomy state
  const [availableDocTypes, setAvailableDocTypes] = useState<DocTypeWithMetadata[]>([]);
  const [loadingDocTypes, setLoadingDocTypes] = useState(false);
  // Distinguish "doc-types fetch failed" from "loaded but empty". Without this an
  // API error collapses to an empty list, which silently suppresses every doc-type
  // affordance AND the WOL-330 mismatch banner (it guards on length > 0), leaving
  // the user stranded with no guidance.
  const [docTypesError, setDocTypesError] = useState(false);
  // OCR-detected lead document class (e.g. "Complaint"), kept in-session only.
  // Drives a non-committal case-type *hint* in the picker (WOL-329 item 4) — it
  // NEVER auto-selects a type (we can't know the specific JTI case type from a
  // complaint body; the cover-sheet / CM-010 carries it and is often unbundled).
  const [detectedLeadDocClass, setDetectedLeadDocClass] = useState<string | null>(null);

  // Motion/OSC code-list values for the MOTION_OSC_DETAIL codeList metadata field
  // (e.g. a subsequent "Motion" doc). JTI treats this field as a code list; free
  // text is rejected with [4061] Invalid MetaData Code value. Sourced from policy
  // via case-fields, keyed on the leaf caseType.code (= JTI CASE_CATEGORY).
  const [motionOscOptions, setMotionOscOptions] = useState<{ code: string; name: string }[]>([]);

  const hasLeadDocument = documents.some(d => d.isLeadDocument);

  // Fetch document types when category, case type, or filing type changes
  /* eslint-disable @eslint-react/set-state-in-effect -- synchronous reset + loading flag before async fetch */
  useEffect(() => {
    if (!caseCategoryName || !caseType?.code || !filingType) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
      setAvailableDocTypes([]);
      return;
    }

    setLoadingDocTypes(true);
    setDocTypesError(false);
    const params = new URLSearchParams({
      categoryName: caseCategoryName,
      caseType: caseType.code,
      filingType,
      ...(county ? { county } : {}),
      ...(caseCategoryCode ? { categoryCode: caseCategoryCode } : {}),
    });

    fetchWithAuth(`${API_BASE}/api/efiling/reference/document-types?${params}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(json => {
        // R100 (WOL-446): patch the served policy with the county's court-
        // required Refers-To before anything downstream (registration, the
        // per-doc form, DocumentAssignmentSection) reads it — one chokepoint
        // keeps rendering and validation consistent. No-op unless the confirm
        // flow is on and the county has a correction.
        setAvailableDocTypes(
          applyRefersToRequirement(json.data || [], county, confirmFlowEnabled),
        );
      })
      .catch(() => {
        setAvailableDocTypes([]);
        setDocTypesError(true);
      })
      .finally(() => setLoadingDocTypes(false));
  }, [caseCategoryCode, caseCategoryName, caseType?.code, filingType, county, fetchWithAuth, confirmFlowEnabled]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  // Fetch Motion/OSC code-list options for the MOTION_OSC_DETAIL codeList field.
  // Keyed on the leaf caseType.code (= JTI CASE_CATEGORY) which the motion list relates to.
  /* eslint-disable @eslint-react/set-state-in-effect -- synchronous reset before async fetch */
  useEffect(() => {
    if (!caseCategoryCode || !caseType?.code) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
      setMotionOscOptions([]);
      return;
    }
    const params = new URLSearchParams({
      ...(county ? { county } : {}),
      categoryCode: caseCategoryCode,
      caseTypeCode: caseType.code,
    });
    let cancelled = false;
    fetchWithAuth(`${API_BASE}/api/efiling/reference/case-fields?${params}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (!cancelled) setMotionOscOptions(data?.motionOscOptions || []); })
      .catch(() => { if (!cancelled) setMotionOscOptions([]); });
    return () => { cancelled = true; };
  }, [caseCategoryCode, caseType?.code, county, fetchWithAuth]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  // Code-list options threaded into DocumentMetadataForm, keyed by metadata code.
  const codeListOptions = useMemo(
    () => ({ MOTION_OSC_DETAIL: motionOscOptions }),
    [motionOscOptions],
  );

  // Publish each doc type's required-metadata items to the context so tab
  // validation (and the fee-calc readiness gate) can enforce required metadata
  // — the policy items aren't wizard state, so this fetch is their only source
  // (Walter 2026-07-10 R6/R7).
  useEffect(() => {
    const map: Record<string, DocumentMetadataItem[]> = {};
    for (const dt of availableDocTypes) {
      map[String(dt.id)] = dt.metadata || [];
    }
    registerDocTypeMetadata(map);
  }, [availableDocTypes, registerDocTypeMetadata]);

  // Determine if we're in case initiation mode
  const isInitiation = filingType === 'case-initiation';

  // R81/R82 (Walter): offer extraction-sourced roster entries in the
  // existing-data selectors (and the R58/R83 attorney auto-pick) whenever the
  // filing is NOT subsequent — on a case-initiation there is no court roster
  // for an OCR-minted entry to shadow, and Case Participants displays them, so
  // the selectors must offer the same set. Subsequent keeps the R6 exclusion.
  const offerExtractionEntries = filingType !== 'subsequent-filing';

  // SPEC-SOR-220-v2 R6: the roster's origin, decided ONCE by the shared
  // predicate — court-sourced = a subsequent filing whose roster carries the
  // court's own record. On that path the FILING_PARTY plaintiff-role fill is
  // suppressed in BOTH flows: the guard keys on this data, not on which flow
  // is rendering.
  const courtSourcedRoster = isCourtSourcedRoster({ filingType, parties });

  // WOL-349: lead eligibility is formGroups-gated for case initiation ONLY
  // (the EFCI_LEAD policy data is reliable there). Madera's subsequent doc
  // types mostly carry EMPTY formGroups (430220 Association, 448140 POS of
  // Summons → []), so gating the lead slot on EF_LEAD dead-ended every
  // subsequent filing — the radio was disabled on all docs and stage-1
  // validation demanded a lead. JTI itself requires exactly one lead
  // regardless of formGroups, so in subsequent mode any doc may be the lead.
  const isLeadEligibleDoc = useCallback(
    (formGroups: string[] | undefined): boolean =>
      isInitiation ? (formGroups || []).includes('EFCI_LEAD') : true,
    [isInitiation],
  );

  // Filter doc types by formGroup rules.
  const formGroupFilteredDocTypes = availableDocTypes.filter(dt => {
    const fg = dt.formGroups || [];
    if (isInitiation) {
      // Case initiation: the first upload must be the case-initiating lead
      // (EFCI_LEAD); once a lead exists, additional EFCI docs unlock.
      return hasLeadDocument
        ? fg.includes('EFCI') || fg.includes('EFCI_LEAD')
        : fg.includes('EFCI_LEAD');
    }
    // WOL-342: Subsequent (Existing-case) filings — show the FULL policy doc-type
    // list for browsing even before a lead is chosen. Previously we collapsed to
    // EF_LEAD-only pre-lead, which hid every non-lead doc (Proof of Service, etc.)
    // and read to filers as "the doc type is missing." Lead eligibility (EF_LEAD)
    // is still enforced on the lead slot itself — handleSetLeadDocument rejects a
    // non-EF_LEAD pick, and the auto-lead `isLeadEligible` guard in ingestFiles
    // only promotes an EF_LEAD-eligible first upload.
    return true;
  });

  // WOL-396: on a subsequent filing, steer away from case-initiation-only
  // ("Initial …") doc types when a non-initial counterpart exists — e.g. drop
  // "Initial Request for Order" in favor of the plain "Request for Order" (the
  // Madera cert Sub #3 kickback). No-op for case initiation.
  // SOR-160: the free-text narrowing that used to live at the step level now
  // lives INSIDE each card's DocTypeCombobox (instance-local search), so
  // initiationSteeredDocTypes is the per-card option source directly — no
  // shared step-level filteredDocTypes.
  const initiationSteeredDocTypes = steerAwayFromInitiationOnly(formGroupFilteredDocTypes, !isInitiation);

  // WOL-330: case-type / doc-class mismatch. A case type was chosen and its
  // doc-type list loaded, but none of those types are case-initiating leads
  // (EFCI_LEAD) — so the user can't file the complaint/petition they just
  // uploaded to open a case. Distinct from the cold-start strand (no type set):
  // here a type IS set, it's just the wrong one. Steer them to a different type.
  const noLeadInitiatingTypes =
    isInitiation &&
    !hasLeadDocument &&
    !!caseType?.code &&
    !loadingDocTypes &&
    availableDocTypes.length > 0 &&
    formGroupFilteredDocTypes.length === 0;

  // WOL-335 — sibling to noLeadInitiatingTypes. A document IS uploaded, but its
  // OCR-detected class (e.g. "Complaint") is absent from the doc types the
  // selected case type offers — so the user is about to mis-file (Walter: a
  // Complaint + Small Claims silently narrowed the list to "Plaintiff's Claim"
  // with no warning). Gate on a doc being UPLOADED (documents.length), NOT on it
  // being the marked lead — in the cold-start flow the doc isn't flagged lead
  // until a type is assigned, which can't happen for a mismatched case type.
  const detectedClassNotOffered = shouldWarnDocClassNotOffered({
    isInitiation,
    documentCount: documents.length,
    detectedLeadDocClass,
    caseTypeCode: caseType?.code,
    loadingDocTypes,
    availableDocTypes,
  });

  // Shared ingest path for both the file <input> and the cold-start drop zone.
  const ingestFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;

    // Check for duplicate filenames
    const existingNames = new Set(documents.map(d => d.fileName.toLowerCase()));
    const skipped: string[] = [];

    incoming.forEach((file, index) => {
      if (existingNames.has(file.name.toLowerCase())) {
        skipped.push(file.name);
        return;
      }
      existingNames.add(file.name.toLowerCase());
      const localId = crypto.randomUUID();
      // Subsequent mode: always eligible (WOL-349) → the first upload
      // auto-designates as lead even before a doc type is chosen. In initiation
      // it starts non-lead and is promoted once a lead-eligible type is chosen
      // on the card. SOR-157 EARS-02: there is no top picker to pre-fill type /
      // title / formGroups / metadata from — the per-card <select> is the single
      // source of truth, so a new upload enters typeless.
      const isLeadEligible = isLeadEligibleDoc(undefined);
      const newDoc: EFilingDocument = {
        id: localId,
        name: file.name,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        isLeadDocument: !hasLeadDocument && index === 0 && documents.length === 0 && isLeadEligible,
        metadataValues: {},
        isPrivate: false,
      };
      addDocument(newDoc);

      // Upload to server in background (status tracked in context)
      // On success, reconcile wizard state doc ID with server-assigned DB ID
      uploadDocument(file).then((serverDoc) => {
        updateDocument(localId, { id: serverDoc.id });
      }).catch(() => {
        // Error status tracked via uploadStatuses in context
      });
    });

    // Show duplicate warning if any files were skipped
    if (skipped.length > 0) {
      setDuplicateWarning(`Already uploaded: ${skipped.join(', ')}`);
      setTimeout(() => setDuplicateWarning(null), 5000);
    } else {
      setDuplicateWarning(null);
    }

    // Trigger OCR extraction for the first non-skipped PDF
    const extractableFile = incoming.find(
      f => !skipped.includes(f.name) && f.type === 'application/pdf'
    );
    // Re-run OCR whenever the first document enters the (possibly re-emptied) draft,
    // regardless of prior extractionStatus — fixes remove-then-reupload skip.
    if (extractableFile && documents.length === 0) {
      triggerExtraction(extractableFile);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    ingestFiles(Array.from(files));
    // Reset file input so the same file can be re-selected later
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Cold-start drop zone: shown when the wizard has no documents and no
  // document-type list yet (case category/type not set). This is the
  // document-first entry point — drop a complaint, OCR fills the rest.
  // WOL-345: when category + case type are set but the API returned 0 doc types, show the
  // empty-state banner (below) instead of the cold-start dropzone. Without this guard the
  // dropzone hides the banner and misleads the filer into thinking they just need to upload.
  const noDocTypesForCombo =
    !!caseCategoryName &&
    !!caseType?.code &&
    !loadingDocTypes &&
    availableDocTypes.length === 0 &&
    !docTypesError;

  const showColdStartDropzone =
    documents.length === 0 && availableDocTypes.length === 0 && !noDocTypesForCombo;

  // The case category/type selection lives in EXACTLY ONE place on this step:
  // inside the "Case Category & Type" card, in the step's normal top-to-bottom
  // flow (Walter 2026-07-10 R9–R11 — the former "Set your case type to
  // continue" box above the upload controls is gone). The in-card picker shows
  // whenever the selection is missing or needs re-picking:
  //   - no resolved case type (OCR miss, WOL-329)
  //   - type set but it can't initiate a case (WOL-330)
  //   - uploaded doc's detected class isn't offered by the type (WOL-335)
  //   - court offers no doc types for the type at all (WOL-345)
  // Like the court picker below, the case-type picker STAYS mounted after a
  // manual pick (Walter 2026-07-10 R21): when OCR never read the category/type,
  // the card's own detected-lines display can't fire, so the selects double as
  // the display of the selection — without this the card collapsed back to
  // "Not detected / Set case type →" the moment the selection landed, reading
  // as if the choice didn't register.
  const caseTypeOcrDetected =
    ocrPopulatedFields.has('caseCategoryName') || ocrPopulatedFields.has('caseTypeName');
  // SOR-173: the recovery banner and its candidate chips live INSIDE the
  // in-card picker, so the card has to mount the picker for them to exist at
  // all. Every condition below that used to vouch for that on a rejected
  // category went silent when the rejected name stopped being written to
  // `caseCategoryName`: `noDocTypesForCombo` needs a name to have fetched
  // against, and `caseTypeOcrDetected` is satisfied by the case TYPE alone — so
  // an extraction that resolved the type but not the category (the Madera
  // incident exactly) would leave the recovery unmounted and the filer stuck
  // with a category that cannot load a document-type list. The rejection is
  // declared state; read it rather than infer it from the fallout.
  const categoryRejected = !!ocrExtractedCategoryName && !caseCategoryCode;
  const needsCaseTypePicker =
    !caseType?.code ||
    categoryRejected ||
    noLeadInitiatingTypes ||
    detectedClassNotOffered ||
    noDocTypesForCombo ||
    !caseTypeOcrDetected ||
    caseTypePickerForced;

  // R86 (Walter, WOL-443): on an existing-case filing the looked-up case
  // DEFINES the category and type — the card must not invite re-entering
  // them. Before the attach it points at the case lookup below; after, it
  // names the case they came from. R108 (WOL-460) extended this from the
  // confirm flow to BOTH flows: classic used to require attaching on Where
  // to File and back-tracking here for the doc type.
  const subsequentUpload = filingType === 'subsequent-filing';

  // SOR-162: detach the attached case on the Upload step so a wrong court/case
  // is correctable in place — never only by advancing to Confirm and back. The
  // wipe reuses the SAME shared helper as the Where-to-File county change
  // (SOR-154), clearing the case + its imported `case-roster` parties/attorneys,
  // then remounts the search panel (nonce) to re-open a clean lookup.
  //
  // SPEC-SOR-174 R17's ctx is deliberately NOT threaded here: this affordance
  // detaches the attached CASE, it does not change the filing court (the county
  // is only writable through InlineCourtSelector, mounted on this same step,
  // which does carry it). R17's antecedent is a court change, and the
  // case-change reconcile is a separate obligation that RETAINS each document
  // with its party references remapped — clearing doc types here would pre-empt
  // it with the opposite behaviour.
  //
  // R18's section revert, by contrast, DOES belong here: its antecedent is a
  // change of attached case, which is exactly what this affordance performs.
  // The wipe above destroys the content the filer confirmed under the old case,
  // so without the revert `sec_case`/`sec_parties`/`sec_documents` would keep
  // reading `confirmed` — and keep their confirm acts in
  // `filerConfirmedSections` — over emptied state. `CaseAttachPanel`'s attach
  // path (the other half of the same user intent) reverts them for the same
  // reason. `sec_court` survives here as it does there: the filing court did
  // not change. Confirm-flow gated, like every other guided-section write (R16
  // keeps the model inert in the classic flow).
  const handleChangeCaseFromUpload = useCallback(() => {
    resetCourtScopedCaseData({
      caseTrackingId,
      updateCaseInfo,
      setParties,
      setAttorneys,
      // Stated, not defaulted: the court is UNCHANGED here — this affordance
      // changes the case. Same court on both sides is the honest transition and
      // is a second reason (beside the absent `confirmFlowEnabled`) that the
      // R17 leg cannot fire from this site.
      previousCourt: { state: filingState, county },
      nextCourt: { state: filingState, county },
    });
    // SPEC-SOR-220-v2: the detach is a case change, so it owes the same clear
    // the attach stamps — every party-class slot holding a reference to the
    // roster being emptied is emptied THROUGH the writer and stamped `cleared`
    // (write kind 'case_change_clear'; the writer stamps only slots that land
    // empty, so an untouched scalar or an already-empty slot is neither
    // rewritten nor claimed as cleared). Without this the stale references
    // survive into the next case, and only the filer's own act resolves a
    // cleared slot — the guarded gap-fill refuses it everywhere.
    for (const doc of documents) {
      const holdsAny = PARTY_CLASS_CODES.some(code => holdsPartyReference(doc.metadataValues?.[code]));
      if (!holdsAny) continue;
      updateDocumentMetadata(doc.id, prev => {
        const clearPatch: Record<string, unknown> = {};
        for (const code of PARTY_CLASS_CODES) {
          if (holdsPartyReference(prev[code])) clearPatch[code] = [];
        }
        return { ...prev, ...clearPatch };
      }, 'case_change_clear');
    }
    if (confirmFlowEnabled) revertCaseAttachSections();
    setCaseSearchNonce(n => n + 1);
  }, [caseTrackingId, updateCaseInfo, setParties, setAttorneys, filingState, county, confirmFlowEnabled, revertCaseAttachSections, documents, updateDocumentMetadata]);

  const caseTypeSlot = subsequentUpload ? (
    <p
      data-testid="case-type-from-case"
      className="text-xs mb-3"
      style={{ color: brand.colors.textLight }}
    >
      {wizardState.caseInfo.caseTrackingId
        ? `Defined by your existing case${wizardState.caseInfo.trialCourtCaseNumber ? ` (${wizardState.caseInfo.trialCourtCaseNumber})` : ''}${caseCategoryName ? ` — ${caseCategoryName}` : ''}.`
        : 'Your existing case defines the category and type — find your case below and they fill in automatically.'}
    </p>
  ) : needsCaseTypePicker ? (
    <InlineCaseTypeSelector
      brand={brand}
      reason={
        noLeadInitiatingTypes
          ? 'no-lead-types'
          : detectedClassNotOffered
            ? 'doc-class-mismatch'
            : noDocTypesForCombo
              ? 'no-doc-types'
              : 'not-detected'
      }
      detectedLeadDocClass={detectedClassNotOffered || CASETYPE_HINT_ENABLED ? detectedLeadDocClass : null}
    />
  ) : undefined;

  // The court is the ROOT of the document-typing dependency chain — county →
  // case categories/types → document types — so when OCR didn't read it, the
  // Court card hosts the picker and the whole chain completes on this step,
  // top-to-bottom, strictly forward (Walter 2026-07-10 R14–R17; it used to
  // require jumping ahead to Where to File and coming back). The picker stays
  // once a court is chosen (the selects double as the display), so the
  // selection remains visible and changeable in place.
  const needsCourtPicker = !ocrPopulatedFields.has('county') || courtPickerForced;
  const courtSlot = needsCourtPicker ? <InlineCourtSelector brand={brand} /> : undefined;

  // Render the confirm-cards block after any upload, not only after a
  // successful OCR pass — when extraction fails (or produces nothing), the
  // Court and Case Category & Type cards are still the one place to set the
  // court, filing type, and case type (R14–R16: the strictly-forward path
  // cannot depend on OCR succeeding).
  const showConfirmCards =
    extractionStatus === 'extracted' ||
    ocrPopulatedFields.size > 0 ||
    (documents.length > 0 && (needsCaseTypePicker || needsCourtPicker || !filingType));

  // Shared drop handler — the cold-start dropzone AND the post-upload upload
  // area both accept drag-and-drop (Walter R25), alongside the file picker.
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
    ingestFiles(dropped);
  };

  const triggerExtraction = useCallback(async (file: File) => {
    setExtractionStatus('extracting');
    setExtractionNotice(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetchWithAuth(`${API_BASE}/api/efiling/extract`, {
        method: 'POST',
        body: formData,
        headers: {},
      });
      if (!response.ok) {
        throw new Error(`Extraction failed (${response.status})`);
      }
      const result: ExtractionResult = await response.json();
      // Stash the detected lead doc class (e.g. "Complaint") for the case-type hint.
      setDetectedLeadDocClass(result.patch.documentType?.name ?? null);
      applyExtraction(result.patch);
      // WOL-331: image-only PDFs get rejected by the court, so we convert them
      // to a text-searchable copy at submit time (keeping the original). Tell the
      // filer up front so the substituted document isn't a surprise. Stays until
      // the next extraction (triggerExtraction clears it on entry).
      if (result.provenance?.searchable === false) {
        setExtractionNotice(
          "This document isn't text-searchable. We'll prepare a court-compliant searchable copy for filing and keep your original.",
        );
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      setExtractionStatus('failed');
      setExtractionNotice('OCR extraction failed — you can fill in fields manually.');
      setTimeout(() => setExtractionNotice(null), 8000);
    }
  }, [fetchWithAuth, applyExtraction, setExtractionStatus]);

  const handleSetLeadDocument = (docId: string) => {
    const targetDoc = documents.find(d => d.id === docId);
    if (!targetDoc) return;
    // Only allow docs with the correct formGroup to be set as lead
    // (initiation mode only — subsequent mode accepts any doc, WOL-349)
    if (!isLeadEligibleDoc(targetDoc.formGroups)) return;

    documents.forEach(doc => {
      if (doc.id === docId) {
        updateDocument(doc.id, { isLeadDocument: true });
      } else if (doc.isLeadDocument) {
        updateDocument(doc.id, { isLeadDocument: false });
      }
    });
  };

  // Auto-populate metadata values based on party roles. `doc` is the document
  // being seeded, when one exists — the ONE guarded decider consults ITS data
  // (provenance + roster origin), so a type re-applied on a court-sourced or
  // cleared draft doesn't re-fill what the guard refuses.
  // SPEC-SOR-258-v1 (the cutover): every party-class value this seed writes
  // comes from the single buildAssignmentFillPatch call below — the former
  // inline REFERS_TO_PARTIES / PARTY_SERVED / FORMER_ATTORNEY /
  // FILING_ATTORNEY arms are gone, so one widening or narrowing of a refusal
  // is one widening or narrowing here, in the roster backstop below and in
  // the About Your Case assignment section alike. Callers pass 'gapfill' so
  // each seeded slot records `prefilled`, never a laundered `filer_chosen`.
  const buildAutoMetadataValues = useCallback((docType: DocTypeWithMetadata, doc?: EFilingDocument): Record<string, unknown> => {
    const meta = docType.metadata || [];
    const values: Record<string, unknown> = {};

    // The REFERS_TO_PARTIES scaffold — the synthetic field (not from JTI
    // metadata) is key-presence-gated twice: the decider's leg fires only when
    // the key EXISTS, and the form renders the item only when it is defined.
    // So the key is BORN here, exactly as the deleted arm birthed it — same
    // arming condition, same empty array when no non-filing parties exist.
    // This is not a party-class VALUE decision: the ids (the value) come only
    // from the decider below, which sees this scaffold.
    const hasExplicitRefersTo = meta.some(m =>
      m.code === 'RESPONDING_PARTY' || m.code === 'PARTY_SERVED' || m.code === 'REPRESENTING'
    );
    if (!hasExplicitRefersTo && meta.some(m => m.code === 'FILING_PARTY')) {
      values.REFERS_TO_PARTIES = [];
    }

    // SPEC-SOR-220-v2: the ONE decider, over the doc's own values + the
    // scaffold above, with the doc's provenance riding alongside — the same
    // refusals (a cleared slot; a FILING_PARTY minted over a court-sourced
    // roster) as the roster backstop below and the About Your Case assignment
    // section.
    const fill = buildAssignmentFillPatch({
      metadataValues: { ...(doc?.metadataValues || {}), ...values },
      metadataItems: meta,
      parties,
      attorneys: wizardState.attorneys || [],
      offerExtractionEntries,
      assignmentProvenance: doc?.assignmentProvenance,
      courtSourcedRoster,
    });
    Object.assign(values, fill);

    // Auto-fill SERVICE_DATE with today's date — not a party-class slot, so it
    // keeps its inline form and the writer never stamps it.
    if (meta.some(m => m.code === 'SERVICE_DATE')) {
      values.SERVICE_DATE = new Date().toISOString().slice(0, 10);
    }

    return values;
  }, [parties, wizardState.attorneys, offerExtractionEntries, courtSourcedRoster]);

  // The single writer that takes a card OUT of "Change" mode (SOR-167). The Set
  // was add-only, so a card that entered edit mode stayed there for the
  // component's lifetime: the summary — the only element that displays the
  // chosen type — never rendered again, and the pick appeared to "take" only
  // after a refresh remounted the tab and re-initialised the Set. Both exits
  // (committing a selection, and the Cancel affordance next to the combobox)
  // go through here.
  const leaveDocTypeEditMode = useCallback((docId: string) => {
    setEditingDocType(prev => {
      if (!prev.has(docId)) return prev;
      const next = new Set(prev);
      next.delete(docId);
      return next;
    });
  }, []);

  const handleSelectDocType = useCallback((docId: string, docType: DocTypeWithMetadata | null) => {
    if (!docType) {
      updateDocument(docId, {
        documentType: undefined,
        documentTitle: undefined,
        formGroups: undefined,
        metadataValues: {},
      });
      return;
    }
    // Auto-mark as lead if no lead exists and this type is eligible
    // (subsequent mode: always eligible, WOL-349)
    const shouldAutoLead = !hasLeadDocument && isLeadEligibleDoc(docType.formGroups);
    updateDocument(docId, {
      documentType: { code: String(docType.id), name: docType.name },
      documentTitle: undefined,
      formGroups: docType.formGroups || [],
      ...(shouldAutoLead ? { isLeadDocument: true } : {}),
    });
    // Seed auto-derived metadata UNDER any existing user-set entries (AC#2): a
    // re-applied doc type (e.g. on remount, or re-picking the same type) must
    // never wipe values the filer already set. Auto values fill only the gaps;
    // user entries win. Stale keys from a genuine type switch are harmless —
    // getMetadataForDoc filters render by the new type's metadata.
    // The seed consults the guarded builder over THIS document's data
    // (SPEC-SOR-220-v2), so its provenance and the roster's origin travel with
    // the seed — and the write is a 'gapfill' (SPEC-SOR-258-v1): every seeded
    // party-class slot records `prefilled`, never a laundered `filer_chosen`.
    const doc = documents.find(d => d.id === docId);
    updateDocumentMetadata(docId, prev => ({ ...buildAutoMetadataValues(docType, doc), ...prev }), 'gapfill');
    // Leave "Change" mode now that a type is chosen (SOR-167), so the card
    // returns to the summary and displays the type just picked. Not done in the
    // null branch above: clearing the type leaves the picker up by itself
    // (documentType is undefined, so the render gate's left operand is already
    // falsy) and the filer needs it to choose a replacement.
    leaveDocTypeEditMode(docId);
  }, [updateDocument, updateDocumentMetadata, buildAutoMetadataValues, isLeadEligibleDoc, hasLeadDocument, leaveDocTypeEditMode, documents]);

  // Reconcile + re-populate per-doc metadata references when either roster
  // changes. Selecting an existing JTI case (OrderInfoTab) swaps the party AND
  // attorney rosters wholesale, minting fresh crypto.randomUUID IDs — but never
  // touches per-doc metadataValues, so existing-data references (FILING_PARTY,
  // REFERS_TO_PARTIES, FORMER_ATTORNEY, …) are left pointing at orphaned pre-swap
  // UUIDs (SOR-210). Two passes per doc, in this order:
  //   (1) DROP every existing-data reference ID that no longer resolves to a
  //       current-roster member — field-uniform, classified by valueRestriction
  //       + classType (party-class → party roster, attorney-class → attorney
  //       roster), never a hardcoded code list (the orphan accumulation covaries
  //       with write order, not field identity, so the fix must be uniform).
  //   (2) the empty-fill that re-populates what the drop emptied — routed
  //       through the ONE guarded builder (SPEC-SOR-220-v2), so its refusals
  //       (a cleared slot; a FILING_PARTY fill over a court-sourced roster)
  //       apply here too.
  // Drop MUST precede fill (SOR-210 I3): a FILING_PARTY full of ghost IDs is
  // non-empty, so fill-first would skip it and the later drop would empty it with
  // no re-fill. Both passes build one nextMv and issue a SINGLE updateDocument per
  // doc (only when it actually changed) so they compose without clobbering.
  // availableDocTypes is a dep so reconciliation re-runs once a post-swap doc-type
  // metadata reload classifies the fields.
  useEffect(() => {
    if (parties.length === 0) return;
    const partyIds = new Set(parties.map(p => p.id));
    const attorneyIds = new Set((wizardState.attorneys || []).map(a => a.id));

    for (const doc of documents) {
      if (!doc.documentType?.code) continue;
      // Reuse getMetadataForDoc so the synthetic REFERS_TO_PARTIES item (not in
      // dt.metadata) is reconciled too. Runs post-render → const is initialized.
      // eslint-disable-next-line react-hooks/immutability -- runs post-render via callback, const is initialized (see comment above)
      const meta = getMetadataForDoc(doc);
      const mv = doc.metadataValues || {};
      // Collect ONLY the keys this reconciliation changes (drop + empty-fill), then
      // merge that patch onto the LATEST committed metadataValues via the functional
      // updater. A stale-snapshot wholesale replace (`metadataValues: {...mv}`) would
      // drop a value the filer set in the same render window but that the effect
      // never touches — e.g. a just-toggled boolean reverting after tab navigation
      // (SOR-209). Reads below evaluate post-drop values via `patch`-or-`mv`.
      const patch: Record<string, unknown> = {};
      let changed = false;

      // Pass 1 — drop unresolvable existing-data references (field-uniform).
      for (const item of meta) {
        if (item.valueRestriction !== 'existing-data') continue;
        const roster = item.classType === 'caseAssignment'
          ? attorneyIds
          : item.classType === 'caseParticipant'
            ? partyIds
            : null;
        if (!roster) continue;
        const current = Array.isArray(mv[item.code])
          ? (mv[item.code] as unknown[]).filter((v): v is string => typeof v === 'string')
          : [];
        const filtered = current.filter(id => roster.has(id));
        if (filtered.length !== current.length) {
          patch[item.code] = filtered;
          changed = true;
        }
      }

      // Pass 2 — the empty-fill, routed through the ONE guarded builder
      // (SPEC-SOR-220-v2). All three legs consult it against the post-drop
      // values, so what the drop emptied is re-filled only where the guard
      // allows: never into a `cleared` slot, and never a FILING_PARTY minted
      // from plaintiff roles while the roster is court-sourced (R6 — the guard
      // keys on DATA, so this holds in the classic flow exactly as in the
      // guided one). The attorney carve-out is untouched: over a court-sourced
      // roster an unambiguous sole pick still fires.
      const fillPatch = buildAssignmentFillPatch({
        metadataValues: { ...mv, ...patch },
        metadataItems: meta,
        parties,
        attorneys: wizardState.attorneys || [],
        offerExtractionEntries,
        assignmentProvenance: doc.assignmentProvenance,
        courtSourcedRoster,
      });
      for (const code of Object.keys(fillPatch)) {
        patch[code] = fillPatch[code];
        changed = true;
      }

      if (changed) {
        // Merge only the reconciled keys; untouched user-set entries come from
        // prev. The merged drop+fill patch is ONE 'gapfill' write
        // (SPEC-SOR-258-v1) — the drop is not a case-change clear (that is the
        // separate remap write, already 'case_change_clear') — so every slot
        // this reconciliation touched records `prefilled`.
        updateDocumentMetadata(doc.id, prev => ({ ...prev, ...patch }), 'gapfill');
      }
    }
  // Run when either roster or the doc-type metadata changes, not on every
  // document update (updateDocument mutates documents, which is intentionally
  // excluded to avoid an update storm).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parties, wizardState.attorneys, availableDocTypes, offerExtractionEntries, courtSourcedRoster]);

  const handleSelectDocTitle = useCallback((docId: string, title: EfmDocumentTitle | null) => {
    if (!title) {
      updateDocument(docId, { documentTitle: undefined });
      return;
    }
    updateDocument(docId, {
      documentTitle: { code: title.code, name: title.name },
    });
  }, [updateDocument]);

  const handleMetadataChange = useCallback((docId: string, code: string, value: unknown) => {
    // Merge against the latest committed metadataValues (functional updater) rather
    // than a `documents` closure snapshot. The old snapshot-merge dropped a value
    // when a second write to the same doc landed in the same render window — e.g.
    // an auto-derived FILING_PARTY rebuild clobbering a just-toggled boolean
    // (SOR-209 UNNAMED_OCCUPANTS revert).
    updateDocumentMetadata(docId, prev => ({ ...prev, [code]: value }));
  }, [updateDocumentMetadata]);

  // Get metadata items for a document based on its type
  // Includes synthetic REFERS_TO_PARTIES item when auto-derived
  const getMetadataForDoc = (doc: EFilingDocument): DocumentMetadataItem[] => {
    if (!doc.documentType?.code) return [];
    const dt = availableDocTypes.find(d => String(d.id) === doc.documentType?.code);
    const meta = [...(dt?.metadata || [])];

    // Add synthetic "Refers To" item if we auto-derived it and there's no
    // explicit one. REFERS_TO_PARTIES itself counts as explicit — the R100
    // county correction (applyRefersToRequirement) injects it into the policy
    // metadata, and pushing the synthetic copy on top would render it twice.
    const hasExplicitRefersTo = meta.some(m =>
      m.code === 'RESPONDING_PARTY' || m.code === 'PARTY_SERVED'
      || m.code === 'REPRESENTING' || m.code === 'REFERS_TO_PARTIES'
    );
    if (!hasExplicitRefersTo && (doc.metadataValues || {})['REFERS_TO_PARTIES'] !== undefined) {
      meta.push({
        code: 'REFERS_TO_PARTIES',
        name: 'Refers To (as to parties)',
        multiple: true,
        classType: 'caseParticipant',
        subType: 'refers-to',
        valueRestriction: 'existing-data',
        additionalInfoTags: [],
      });
    }

    return meta;
  };

  // Get titles for a specific doc type from the pre-fetched data
  const getTitlesForDocType = (docTypeId: string): EfmDocumentTitle[] => {
    const dt = availableDocTypes.find(d => String(d.id) === docTypeId);
    return dt?.titles ?? [];
  };

  return (
    <div>
      <h2
        className="text-lg font-semibold mb-4"
        style={{ color: brand.colors.text }}
      >
        Documents
      </h2>

      {/* Shared hidden file input — used by both the drop zone and the upload button */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        multiple
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Cold-start OCR drop zone — the document-first entry point. Shown when no
          documents are uploaded and no document-type list is available yet. */}
      {showColdStartDropzone && (
        <div className="mb-6">
          <div
            data-testid="ocr-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={handleDrop}
            className="flex flex-col items-center justify-center text-center px-6 py-12 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
            style={{
              borderColor: isDragging ? brand.colors.primary : brand.colors.border,
              backgroundColor: isDragging ? `${brand.colors.primary}08` : brand.colors.bgWhite,
            }}
          >
            <span className="text-3xl mb-3" role="img" aria-hidden="true">📄</span>
            <p className="text-base font-semibold mb-1" style={{ color: brand.colors.text }}>
              Start with your lead document
            </p>
            <p className="text-sm mb-4 max-w-md" style={{ color: brand.colors.textLight }}>
              Drop your complaint or petition (PDF) here and we&apos;ll read it to auto-fill the
              court, parties, and attorneys (we&apos;ll ask for the specific case type if we can&apos;t read it).
              You can review everything before filing.
            </p>
            <span
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Upload a PDF to get started
            </span>
            <p className="text-[11px] mt-3" style={{ color: brand.colors.textLight }}>
              Tip: make sure the PDF is text-searchable for the best results.
            </p>
          </div>
          {duplicateWarning && (
            <p className="text-xs mt-2 font-medium" style={{ color: '#F59E0B' }}>
              {duplicateWarning}
            </p>
          )}
        </div>
      )}

      {/* Upload area — FIRST on the step (Walter R26: upload → court → case
          category/type → per-document type, top to bottom) and drag-and-drop
          enabled (R25), mirroring the cold-start dropzone. */}
      {!showColdStartDropzone && (
        <div id="document-upload-section" className="mb-6">
          <div
            className="p-3 rounded-lg border text-sm mb-4"
            style={{
              borderColor: brand.colors.warning || '#F59E0B',
              backgroundColor: `${brand.colors.warning || '#F59E0B'}08`,
              color: brand.colors.text,
            }}
          >
            To prevent rejection, please ensure that all documents are text searchable.
          </div>
          <div
            data-testid="upload-drop-area"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={handleDrop}
            className="flex flex-col items-center justify-center text-center px-6 py-6 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
            style={{
              borderColor: isDragging ? brand.colors.primary : brand.colors.border,
              backgroundColor: isDragging ? `${brand.colors.primary}08` : brand.colors.bgWhite,
            }}
          >
            <p className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Drag &amp; drop documents here (PDF), or
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Upload from Computer
            </button>
          </div>
          {duplicateWarning && (
            <p className="text-xs mt-2 font-medium" style={{ color: '#F59E0B' }}>
              {duplicateWarning}
            </p>
          )}
        </div>
      )}

      {/* Extraction status — directly under the upload area it reports on */}
      {extractionStatus === 'extracting' && (
        <div
          data-testid="extraction-loading"
          className="p-3 rounded-lg border text-sm mb-4 flex items-center gap-2"
          style={{
            borderColor: brand.colors.primary,
            backgroundColor: `${brand.colors.primary}08`,
            color: brand.colors.text,
          }}
        >
          <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Extracting document data for auto-fill...
        </div>
      )}
      {extractionNotice && (
        <div
          data-testid="extraction-notice"
          className="p-3 rounded-lg border text-sm mb-4"
          style={{
            borderColor: '#F59E0B',
            backgroundColor: '#F59E0B08',
            color: brand.colors.text,
          }}
        >
          {extractionNotice}
        </div>
      )}

      {/* OCR confirmation cards + filing type detection — court, then case
          category/type, ABOVE the document-type controls they unlock (R26).
          Show right after an in-session extraction, whenever a loaded draft
          carries persisted OCR-populated fields (so the cards and their
          confirmed state survive a hard reload), OR whenever the case type
          still needs setting — the Case Category & Type card hosts the in-card
          picker (R9–R11), so it must render even when OCR produced nothing. */}
      {showConfirmCards && (
        <>
          <OcrConfirmCards
            brand={brand}
            county={county}
            state={filingState || 'CA'}
            caseCategoryName={caseCategoryName}
            caseTypeName={caseType?.name || ''}
            caseTypeResolved={!!caseType?.code}
            ocrPopulatedFields={ocrPopulatedFields}
            confirmedFields={wizardState.ocrConfirmedFields || []}
            onConfirm={confirmOcrField}
            onOpenPicker={(cardId) =>
              cardId === 'court' ? setCourtPickerForced(true) : setCaseTypePickerForced(true)
            }
            caseTypeSlot={caseTypeSlot}
            courtSlot={courtSlot}
            useProperCourtName={confirmFlowEnabled}
          />
          <FilingTypeDetection
            brand={brand}
            filingType={filingType}
            caseNumber={wizardState.caseInfo.trialCourtCaseNumber}
            onSwitchFilingType={(newType) => updateOrderInfo({ filingType: newType })}
            confirmFlow={confirmFlowEnabled}
          />
          {/* R86 (WOL-443) + R108 (WOL-460, both flows): the existing-case
              lookup leads — it renders HERE on the Upload step, directly under
              the filing-type banner, so the found case drives category/type
              (and unlocks the doc-type list via the resolved category name)
              before any typing. The county comes from the Court card above;
              the same panel also mounts later (Confirm page / Where to File)
              for anyone who skips it here. */}
          {subsequentUpload && county && (
            <div className="mb-6" data-testid="upload-case-attach">
              {/* SOR-162: once a case is attached, offer a "change court/case"
                  affordance right here so a wrong case is correctable on this
                  step. The search panel stays mounted below (so the attached
                  selection remains visible), and detaching wipes the case +
                  roster and remounts the panel for a fresh lookup. */}
              {caseTrackingId && (
                <div
                  data-testid="upload-case-attached"
                  className="mb-3 flex items-start justify-between gap-3 p-3 rounded-lg border"
                  style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgWhite }}
                >
                  <p className="text-sm" style={{ color: brand.colors.text }}>
                    Filing into case #{trialCourtCaseNumber || caseTrackingId}
                    {caseCategoryName ? ` — ${caseCategoryName}` : ''}
                  </p>
                  <button
                    type="button"
                    data-testid="change-court-case"
                    onClick={handleChangeCaseFromUpload}
                    className="text-xs underline whitespace-nowrap"
                    style={{ color: brand.colors.link }}
                  >
                    Change court/case
                  </button>
                </div>
              )}
              <CaseAttachPanel
                key={`${county}:${caseSearchNonce}`}
                brand={brand}
                anchorId="case-search-upload"
                resolveCategoryName
              />
            </div>
          )}
        </>
      )}

      {/* SPEC-SOR-174 AX3: this step owns the court and case sections, so their
          confirm controls sit here — below the Court and Case Category & Type
          cards and the existing-case lookup that fill them, and in confirmation
          order (court, then case), which is the order `predecessorsConfirmed`
          offers them in. R21: no control for the parties or documents section
          appears on this step, so Upload's Next can never wait on an approval
          whose subject lives a step forward. Confirm-flow only (R16).

          Gated on the FLOW alone, never additionally on `documents.length`:
          `validateTab('documents')` emits `upload-court-section-required` /
          `upload-case-section-required` on an empty draft too, and the blocker
          panel resolves their jump target `#section-confirm-<section>` through
          getElementById — so a control that mounts only once a document exists
          leaves that link pointing at nothing on exactly the draft a filer is
          most likely to be blocked on. Neither section's readiness predicate
          reads `documents`, so both are genuinely confirmable there as well. */}
      {confirmFlowEnabled && (
        <>
          <SectionConfirmControl
            brand={brand}
            section="sec_court"
            description="Check the filing state and county above, then confirm."
          />
          <SectionConfirmControl
            brand={brand}
            section="sec_case"
            description="Check the case details above, then confirm."
          />
        </>
      )}

      {/* SOR-160: the step-level document-type type-ahead (search input, results
          panel, and "Browse all" control) is GONE. There is now exactly one
          document-type control per document — a searchable DocTypeCombobox on
          each card below — so assigning a type is a single action. The free-text
          narrowing that lived here moved into each combobox's instance-local
          search; initiationSteeredDocTypes is the option source. */}

      {/* Uploaded documents */}
      {documents.length === 0 && (
        <p className="text-sm" style={{ color: brand.colors.textLight }}>
          {hasLeadDocument
            ? 'Upload additional documents as needed.'
            : 'Additional options will appear after the lead document is uploaded.'}
        </p>
      )}

      {/* SPEC-SOR-321 R2: the per-card lead-document radios share one
          name="leadDocument" radio group, so ONE ChoiceGroup fieldset wraps the
          whole card list — its legend is visually hidden because no question
          text renders on the step today. */}
      {documents.length > 0 && (
        <ChoiceGroup question="Lead document" hideLegend>
        {documents.map(doc => {
        const fullMetadata = getMetadataForDoc(doc);
        // Confirm flow (WOL-419 / Walter R69): participant-class fields are
        // completed in the Document Assignments section on the Confirm step —
        // participants don't exist yet on Upload, so rendering the fields here
        // demanded values the filer could not supply without leaving the step
        // (the R68 deadlock). Classic flow keeps them here unchanged. The
        // roster-reconciliation effect above still runs over the FULL set.
        const docMetadata = confirmFlowEnabled
          ? fullMetadata.filter(
              m => m.classType !== 'caseParticipant' && m.classType !== 'caseAssignment',
            )
          : fullMetadata;
        // The card's red state must agree with the gate: route through the
        // SAME shared validator Next uses (validateTab → validateDocumentMetadata)
        // instead of a hand-rolled emptiness copy, so its carve-outs — R18
        // self-rep FILING_ATTORNEY, the SOR-235 representation-change
        // FILING_PARTY_ADDRESS relaxation — flow through. The copy demanded
        // values the gate had stopped demanding, painting a passing card red
        // ("Complete all required fields above" with Next enabled — Walter
        // 2026-09-02). Extracted verbatim to lib/document-card-state.ts
        // (SPEC-SOR-258-v1) so the spec witnesses import this exact predicate.
        const hasRequiredMetadata = !documentCardIncomplete(doc, docMetadata, selfRepPartyIds);
        const missingType = !doc.documentType;
        const missingInfo = missingType || !hasRequiredMetadata;
        // The document type can't be chosen until the case type is set (which
        // loads availableDocTypes). In the document-first flow that happens on a
        // later tab, so treat a missing type as deferred guidance, not an error.
        const docTypeDeferred = missingType && availableDocTypes.length === 0;
        // SOR-167 AC#4: while the filer is re-picking, the type control on the
        // card reads as UNSET (DocTypeCombobox binds its input to the search
        // string, not the selection), so the outgoing type's Additional
        // Information fields must not be on screen beside it — that conjunction
        // is the reported mixed state: a required-field error for metadata
        // belonging to a type the filer can no longer see. Gating the section
        // here makes it structurally impossible, because the fields only ever
        // render for doc.documentType and the control only ever renders when
        // that type is absent OR this flag is set. The same reason suppresses
        // the "complete all required fields above" pointer: mid-change it would
        // name nothing on the card. Conditioned on availableDocTypes as well,
        // because the entire type control — Cancel included — is: a card left in
        // the Set when the doc-type list empties out (the case type was cleared
        // in the card above) would otherwise sit on the note below with no
        // control to use and no way back to its requirements.
        const isEditingDocType = availableDocTypes.length > 0 && editingDocType.has(doc.id);
        const showRequiredFieldsHint = missingInfo && (missingType || !isEditingDocType);
        const selectedDocTypeId = doc.documentType?.code;
        const titlesForDoc = selectedDocTypeId ? getTitlesForDocType(selectedDocTypeId) : [];
        // The "Lead Document" radio only does anything once a lead-eligible doc
        // type is assigned (handleSetLeadDocument guards on the formGroup). Until
        // then the radio is disabled + explained, rather than a dead control.
        // Subsequent mode: every doc is eligible (WOL-349) so the radio is live.
        const isLeadEligible = isLeadEligibleDoc(doc.formGroups);
        const cardBorderColor = !missingInfo
          ? brand.colors.border
          : docTypeDeferred
            ? (brand.colors.warning || '#F59E0B')
            : '#EF4444';

        return (
          <div
            key={doc.id}
            className="border rounded-lg p-4 mb-4"
            style={{ borderColor: cardBorderColor }}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="text-sm font-medium" style={{ color: brand.colors.link }}>
                  {doc.name}
                </p>
                <p className="text-xs" style={{ color: brand.colors.textLight }}>
                  {(doc.size / 1024).toFixed(1)} KB
                  {uploadStatuses[doc.fileName] === 'uploading' && (
                    <span style={{ color: brand.colors.link }}> — Uploading...</span>
                  )}
                  {uploadStatuses[doc.fileName] === 'uploaded' && (
                    <span style={{ color: '#10B981' }}> — Uploaded</span>
                  )}
                  {uploadStatuses[doc.fileName] === 'failed' && (
                    <span style={{ color: '#EF4444' }}> — Upload failed</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label
                  className={`flex items-center gap-1 text-xs ${isLeadEligible ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                >
                  <input
                    type="radio"
                    name="leadDocument"
                    checked={doc.isLeadDocument}
                    disabled={!isLeadEligible}
                    onChange={() => handleSetLeadDocument(doc.id)}
                    style={{ accentColor: brand.colors.primary }}
                    // Tooltip + screen-reader hint live on the input itself (not the
                    // label) so the constraint is announced when the disabled control
                    // is focused — it's a status indicator, not a silent dead control.
                    title={
                      isLeadEligible
                        ? 'Mark this as the lead document for the filing'
                        : 'Assign a lead-eligible document type (e.g. Complaint or Petition) first — then you can set this as the lead document'
                    }
                    aria-describedby={!isLeadEligible ? `lead-doc-hint-${doc.id}` : undefined}
                  />
                  <span style={{ color: isLeadEligible ? brand.colors.text : brand.colors.textLight }}>
                    Lead Document
                  </span>
                </label>
                {!isLeadEligible && (
                  <span id={`lead-doc-hint-${doc.id}`} className="sr-only">
                    Assign a lead-eligible document type, such as a Complaint or Petition, before marking this as the lead document.
                  </span>
                )}
                <button
                  onClick={() => removeDocument(doc.id)}
                  className="text-xs text-red-500 underline"
                >
                  Remove
                </button>
              </div>
            </div>

            {/* R78 (WOL-433, confirm flow): the lead is not an eligible
                initiating document for a new case — say so AT the document
                with the fix, instead of only disabling Next. Reachable when
                the lead's TYPE is changed after auto-lead (the flag stays). */}
            {confirmFlowEnabled &&
              doc.isLeadDocument &&
              doc.documentType &&
              filingType === 'case-initiation' &&
              !isLeadEligible && (
                <p
                  data-testid={`lead-ineligible-${doc.id}`}
                  role="alert"
                  className="mb-3 p-2 rounded text-xs font-medium"
                  style={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    color: '#991B1B',
                  }}
                >
                  This document type can&apos;t start a new case. Choose an eligible
                  initiating document (like a Complaint or Petition) as the Lead
                  Document, or change this document&apos;s type.
                </p>
              )}

            {/* DOCUMENT TYPE & TITLE */}
            {availableDocTypes.length > 0 && (
              doc.documentType && !isEditingDocType ? (
                <div className="mb-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p
                        className="text-xs font-semibold mb-1 uppercase"
                        style={{ color: brand.colors.textLight }}
                      >
                        Document Type
                      </p>
                      <p className="text-sm" style={{ color: brand.colors.text }}>
                        {doc.documentType.name}
                      </p>
                    </div>
                    <div>
                      <p
                        className="text-xs font-semibold mb-1 uppercase"
                        style={{ color: brand.colors.textLight }}
                      >
                        Document Title
                      </p>
                      <p className="text-sm" style={{ color: brand.colors.text }}>
                        {doc.documentTitle?.name || '\u2014'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingDocType(prev => new Set(prev).add(doc.id))}
                    className="text-xs underline mt-1"
                    style={{ color: brand.colors.link }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="mb-3">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Document Type */}
                    <div>
                      <p
                        className="text-xs font-semibold mb-1 uppercase"
                        style={{ color: brand.colors.textLight }}
                      >
                        Document Type
                      </p>
                      {/* SOR-160: one searchable combobox per card — the single
                          document-type control. Selecting an option fires
                          handleSelectDocType immediately (one action). Its
                          instance-local search means typing here never narrows
                          another card's options. */}
                      <DocTypeCombobox
                        options={initiationSteeredDocTypes}
                        value={selectedDocTypeId}
                        onChange={dt => handleSelectDocType(doc.id, dt)}
                        brand={brand}
                        docId={doc.id}
                      />
                    </div>

                    {/* Document Title */}
                    <div>
                      <p
                        className="text-xs font-semibold mb-1 uppercase"
                        style={{ color: brand.colors.textLight }}
                      >
                        Document Title
                      </p>
                      {/* SOR-157 EARS-03: when the court publishes no title list for
                          the selected type, the title is an OPTIONAL free-form text
                          input (persisted as { code: '', name }) — not a disabled
                          "No titles available" dropdown. Published titles keep the
                          <select>; no type yet keeps the disabled prompt. */}
                      {!selectedDocTypeId ? (
                        <input
                          type="text"
                          disabled
                          placeholder="Select a document type first"
                          /* SPEC-SOR-321-v1 R1: one card per document renders
                             side by side, so each title control is named for
                             its own document, never just "Document title". */
                          aria-label={`Document title for ${doc.name} (select a document type first)`}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                        />
                      ) : titlesForDoc.length === 0 ? (
                        <input
                          type="text"
                          value={doc.documentTitle?.name || ''}
                          placeholder="Document title (optional)"
                          aria-label={`Document title for ${doc.name}`}
                          onChange={e => {
                            const v = e.target.value;
                            updateDocument(doc.id, {
                              documentTitle: v ? { code: '', name: v } : undefined,
                            });
                          }}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                        />
                      ) : (
                        <select
                          value={doc.documentTitle?.code || ''}
                          aria-label={`Document title for ${doc.name}`}
                          onChange={e => {
                            const title = titlesForDoc.find(t => t.code === e.target.value);
                            handleSelectDocTitle(doc.id, title || null);
                          }}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                        >
                          <option value="">Select title...</option>
                          {titlesForDoc.map(title => (
                            <option key={title.code} value={title.code}>
                              {title.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  {/* The way back out of "Change" mode without picking (SOR-167
                      AC#3). Only meaningful when a type is already assigned —
                      an untyped card has nothing to return to, and the picker
                      is its resting state. */}
                  {doc.documentType && (
                    <button
                      onClick={() => leaveDocTypeEditMode(doc.id)}
                      data-testid={`cancel-doc-type-${doc.id}`}
                      className="text-xs underline mt-1"
                      style={{ color: brand.colors.link }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )
            )}

            {/* METADATA-DRIVEN FORM (replaces old Filed By / Refers To grid) */}
            {isEditingDocType ? (
              /* Mid-change the requirements on file belong to the type being
                 replaced, so they come off screen with the summary (AC#4) —
                 leaving them would show a required-field error for metadata of
                 a type the filer can no longer see. Nothing is discarded: the
                 values stay in wizard state and Cancel brings them straight
                 back. */
              <p
                className="text-xs"
                data-testid={`doc-type-editing-note-${doc.id}`}
                style={{ color: brand.colors.textLight }}
              >
                {doc.documentType
                  ? `Choose a document type to see its court requirements, or cancel to keep ${doc.documentType.name} and the information already entered for it.`
                  : 'Court requirements appear once a document type is selected.'}
              </p>
            ) : docMetadata.length > 0 ? (
              <DocumentMetadataForm
                metadata={docMetadata}
                parties={parties}
                attorneys={wizardState.attorneys}
                offerExtractionEntries={offerExtractionEntries}
                values={doc.metadataValues || {}}
                onChange={(code, value) => handleMetadataChange(doc.id, code, value)}
                codeListOptions={codeListOptions}
                brand={brand}
              />
            ) : doc.documentType ? (
              <p className="text-xs" style={{ color: brand.colors.textLight }}>
                No additional court requirements for this document type.
              </p>
            ) : null}

            {docTypeDeferred ? (
              <p
                className="text-xs mt-2 font-medium"
                style={{ color: brand.colors.warning || '#F59E0B' }}
              >
                Set the case type in the Case Category &amp; Type card above to choose this document&apos;s type.
              </p>
            ) : showRequiredFieldsHint && (
              <p className="text-xs mt-2 font-medium text-red-500">
                {missingType
                  ? 'Select a document type'
                  : 'Complete all required fields above'}
              </p>
            )}
          </div>
        );
        })}
        </ChoiceGroup>
      )}
    </div>
  );
};

export default DocumentsTab;
